'use client'

import { useEffect, useRef, useState } from 'react'

import { formatSecondsAsMmSs } from 'shared/utils/date'
import { addTestId } from 'shared/utils/tests'

import { useSupportSessionExit } from './useSupportSessionExit'

import styles from './SupportSessionBar.module.scss'

const TICK_INTERVAL_MS = 1000

interface SupportSessionTimerProps {
  expiresAt: number
}

const getSecondsLeft = (expiresAt: number) =>
  Math.max(0, Math.round((expiresAt - Date.now()) / 1000))

const SupportSessionTimer = ({ expiresAt }: SupportSessionTimerProps) => {
  const exitSupportSession = useSupportSessionExit()
  const [secondsLeft, setSecondsLeft] = useState(() =>
    getSecondsLeft(expiresAt)
  )
  // signOut is not instant, and a throttled tab can fire several ticks at once
  // — without this the session would be torn down more than once.
  const hasExpired = useRef(false)

  useEffect(() => {
    const tick = () => {
      const remaining = getSecondsLeft(expiresAt)

      setSecondsLeft(remaining)

      if (remaining > 0 || hasExpired.current) {
        return
      }

      hasExpired.current = true
      void exitSupportSession()
    }

    tick()

    const interval = setInterval(tick, TICK_INTERVAL_MS)

    // Update the timer when the tab is brought back to the foreground.
    document.addEventListener('visibilitychange', tick)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [expiresAt, exitSupportSession])

  return (
    <span
      className={styles.timer}
      aria-label={`Pozostały czas sesji wsparcia: ${formatSecondsAsMmSs(secondsLeft)}`}
      {...addTestId('support-session-bar-timer')}
    >
      {formatSecondsAsMmSs(secondsLeft)}
    </span>
  )
}

export default SupportSessionTimer
